'use strict';

define('vb/private/translations/bundlesModel',[
  'ojs/ojconfig',
  'vbc/private/utils',
  'vb/private/configuration',
  'vb/private/constants',
  'vb/private/log',
  'vb/private/translations/bundleDefinition',
  'vb/private/translations/bundleUtils',
  'vb/private/translations/bundleV2Definition',
  // JET-69531: Make api for best match to locale public
  '@oracle/oraclejet-preact/utils/UNSAFE_matchTranslationBundle',
], (
  ojConfig,
  Utils,
  Configuration,
  Constants,
  Log,
  BundleDefinition,
  BundleUtils,
  BundleV2Definition,
  UnsafeMatchTranslationBundle,
) => {
  /**
   * @typedef {import('vb/private/translations/bundleUtils').ExtensionTranslationsConfig}
   *   ExtensionTranslationsConfig
   * @typedef {import('vb/private/translations/bundleUtils').TranslationsConfig}
   *   TranslationsConfig
   */

  const logger = Log.getLogger('/vb/private/translations/bundlesModel');

  const PROXY_BUNDLES_STORAGE_ID = 'vbcs.translations.proxyBundles';
  const APPLICATION_PROXY_BUNDLES_STORAGE_ID = `${PROXY_BUNDLES_STORAGE_ID}.${Configuration.applicationUrl}`;

  //
  // Match V2 Local Translations Declarations
  //
  // Matches:
  // self:resourceName-i18n
  // self:$translations/resourceName-i18n
  // extId:$translations/resourceName-i18n
  // global:$translations/resourceName-i18n
  // Note: resourceName could be a simple resource name, or could be some migrated string servlet name like
  // 'oracle.some.servlet.path.resourceName-i18n'
  // with
  // [0]: <the match>
  // [1]: (self|extId|global):$translations/ or self:
  // [2]: self|extId|global or undefined
  // [3]: resourceName-i18n
  //
  // Does Not Match relative paths or hidden files:
  // self:$translations/foo/resourceName-i18n
  // self:$translations/.resourceName-i18n
  //
  // Does Not Match non-self local paths:
  // extId:resourceName-i18n
  // global:resourceName-i18n
  //
  // Does Not Match non-V2 local paths
  // self:resourceName
  // resourceName
  // resourceName-i18n
  //
  // Summary
  // -------
  // Scope:
  // ((self|global|[A-Za-z][A-Z_a-z0-9]{0,63}):\$translations\/|self:)
  // matches
  // <scope>:$translations/ where <scope> is self|global|extId
  // OR
  // self:
  //
  // ResourceName:
  // ((?!\.)[^/]+-i18n)
  // ((?!\.) matches not '.' (negative lookahead, doesn't consume the character)
  // [^\\/]+ matches everything but '\' and '/'
  // This just means no initial '.', no relative path, ends in '-i18n'
  //
  // See https://regex101.com/r/18R0OG/2
  //
  // eslint-disable-next-line max-len
  const V2_LOCAL_DECLARATION_REGEX = /^((self|global|[A-Za-z][A-Z_a-z0-9]{0,63}):\$translations\/|self:)((?!\.)[^\\/]+-i18n)$/;

  /**
   * @typedef V2ExtensionBundleDeclaration
   * @property {string} extId resolved extension id for the bundle provider
   * @property {string} importId original imports.translations key for the declaration
   */

  /**
   * @typedef V2LocalBundleDeclaration
   * @property {string} bundleName
   * @property {string} bundleKey
   * @property {string} extId
   * @property {string} path
   * @property {string} prefix
   * @property {string} resourceName
   * @property {string} localeLocation
   * @property {string} bundleLocation
   * @property {Promise<ExtensionTranslationsConfig>} [extBundleDeclarations]
   */

  /**
   *
   */
  class BundlesModel {
    /**
     * Create a model for BundleDefinitions.
     * Falsy paths should be ignored/skipped (VBS-2097)
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {string} [referencePath] the directory of the referring container
     * @param {Object} [options] Special directives for the bundle definitions
     * @param {Object} [options.initParams] Application's initParams
     * @param {boolean} [options.preferV1] If V1 translations config is present, ignore V2 translations config.
     * @param {boolean} [options.proxyBundles] Make proxies for bundles and don't wait for the bundles to be loaded
     * @param {Object} [options.replacementValues] (V1) map of key/value pairs for expressions in path
     * @param {boolean} [options.allowSelfRelative] (V1) let the container JSON use "./" to mean 'relative to the
     * same folder'
     * @param {boolean} [options.isUnrestrictedRelative] (V1) you can reach outside of your current folder
     * (only app-flow)
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     * @param {Object} [extension] the extension containing this BundlesModel (or null for 'base')
     * @private
     */
    constructor(runtimeEnv, referencePath = '', options = {}, extension = null) {
      this.relativePath = referencePath;
      this.runtimeEnvironment = runtimeEnv;
      this.options = options;

      this.options.proxyBundles = BundlesModel.isProxyBundles(options);

      this.extensionId = (extension && extension.id) || Constants.ExtensionFolders.BASE;

      this.bundleDefinitions = {};
      this.bundleV2Definitions = {};
      this.extensionsBundleV2Definitions = {};

      this.loadV1Promises = null;
      this.loadV2Promises = null;
    }

    /**
     * @private
     */
    addBundleV1Definitions(declarations) {
      // using JET to get locale
      const locale = ojConfig.getLocale();
      Object.entries(declarations)
        .forEach(([name, decl]) => {
          const path = BundleUtils.evaluateTranslationPath(decl, this.options.initParams);
          if (!path) {
            logger.error(`No path declared for translation bundle ${name}`);
          } else if (BundleUtils.isV2(path)) {
            logger.error(`Not a v1 translation bundle ${name}`);
          } else {
            const bundleDefinition = new BundleDefinition(name, path, decl, locale, this.relativePath, this.options);
            this.bundleDefinitions[name] = bundleDefinition;

            // Initiate load of this BundleDefinition, but don't block on it
            if (bundleDefinition.isAllowed()) {
              bundleDefinition.load(this.runtimeEnvironment);
            }
          }
        });
    }

    /**
     * @param {string} extId Id of the extension containing the bundle declarations (may be 'self')
     * @param {Array<string>} bundleNames Names of string bundles for this BundlesModel (the key in the
     * $translations map)
     * @param {Promise<ExtensionTranslationsConfig>} extBundleDeclarations All Bundle Declarations for
     * extId <extension/translations>
     * @private
     */
    addBundleV2Definitions(extId, bundleNames, extBundleDeclarations) {
      bundleNames.forEach((bundleName) => {
        const bundleV2Definition = BundleV2Definition.getBundleV2Definition(BundlesModel.application, this.options,
          (extId === Constants.ExtensionFolders.SELF) ? this.extensionId : extId, bundleName, extBundleDeclarations);

        if (extBundleDeclarations && bundleV2Definition.extBundleDeclarations !== extBundleDeclarations) {
          // Handle rejected declarations when reusing cached bundles.
          extBundleDeclarations.catch(() => {});
        }

        if (extId === Constants.ExtensionFolders.SELF) {
          this.bundleV2Definitions[bundleName] = bundleV2Definition;
        } else {
          if (!this.extensionsBundleV2Definitions[extId]) {
            this.extensionsBundleV2Definitions[extId] = {};
          }
          this.extensionsBundleV2Definitions[extId][bundleName] = bundleV2Definition;
        }

        // Initiate load of this BundleV2Definition, but don't block on it
        bundleV2Definition.load(this.runtimeEnvironment);
      });
    }

    /**
     * @param {V2LocalBundleDeclaration[]} bundleDeclarations Local bundle declarations for this container
     * @private
     */
    addLocalBundleV2Definitions(bundleDeclarations) {
      bundleDeclarations.forEach((bundleDeclaration) => {
        const {
          extId,
          bundleKey,
          extBundleDeclarations,
        } = bundleDeclaration;
        const bundleV2Definition = BundleV2Definition.getBundleV2Definition(BundlesModel.application, this.options,
          extId, bundleKey, extBundleDeclarations);

        if (extBundleDeclarations && bundleV2Definition.extBundleDeclarations !== extBundleDeclarations) {
          // Handle rejected declarations when reusing cached bundles
          extBundleDeclarations.catch(() => {});
        }

        this.bundleV2Definitions[bundleDeclaration.bundleName] = bundleV2Definition;

        // Initiate load of this BundleV2Definition, but don't block on it
        bundleV2Definition.load(this.runtimeEnvironment);
      });
    }

    /**
     * Determines if there are any V2 Bundle Definitions imported from other extensions.
     * @returns {boolean}
     */
    hasExtensionBundleV2Definitions() {
      return Object.keys(this.extensionsBundleV2Definitions).length > 0;
    }

    /**
     * load all bundles, and make a map of name - to - string map
     * @param {boolean} [force] Ensure all bundles are fully loaded, even if they've been proxied
     * @returns {Promise<BundlesModel>}
     */
    load(force) {
      // Load the bundleV1Definitions, make a 'master' map of bundle name -> bundle strings
      this.loadV1Promises = this.loadV1Promises
        || Object.values(this.bundleDefinitions)
          .filter((def) => def.isAllowed())
          .map((def) => def.load(this.runtimeEnvironment));

      // Load the bundleV2Definitions, add to the 'master' map of bundle name -> bundle strings
      if (!this.loadV2Promises) {
        this.loadV2Promises = Object.values(this.bundleV2Definitions)
          .map((def) => def.load(this.runtimeEnvironment));

        // Load the extensionsBundleV2Definitions, make a 'master' map of extensionId -> (bundle name -> bundle strings)
        Object.entries(this.extensionsBundleV2Definitions)
          // eslint-disable-next-line no-unused-vars
          .forEach(([extId, defs]) => {
            this.loadV2Promises.push(...Object.values(defs)
              .map((def) => def.load(this.runtimeEnvironment)));
          });
      }

      const promises = [];

      // If proxying the bundles, don't wait for the load. The map of bundle name -> bundle strings
      // is initialized to the proxied bundles in addBundleV1Definitions & addBundleV2Definitions
      if (force || !this.options.proxyBundles) {
        if (this.loadV1Promises.length) {
          promises.push(...this.loadV1Promises);
        }

        if (this.loadV2Promises.length) {
          promises.push(...this.loadV2Promises);
        }

        return Promise.all(promises)
          .then(() => this);
      }

      return Promise.resolve(this);
    }

    /**
     * Get the (V1) Bundle strings map. Fills bundleMap with the V1 Bundles String Maps.
     * @param {Object.<string, Object>} bundleMap  Map of bundleName to Strings Map
     * @returns {Object.<string, Object>} Map of bundleName to Strings Map
     */
    getStringMap(bundleMap = {}) {
      // For each V1 Bundle, ask it to add its named bundle map to the string map
      Object.values(this.bundleDefinitions)
        .filter((def) => def.isAllowed())
        .forEach((def) => {
          def.addStringMapToBundleMap(bundleMap);
        });
      return bundleMap;
    }

    /**
     * Get the (V1) BundleDefinition with name
     * @param {string} name name of the BundleDefinition
     * @returns {BundleDefinition}
     */
    getBundleDefinition(name) {
      return this.bundleDefinitions[name];
    }

    /**
     * Get the (V2) Bundle strings map. Fills bundleMap with the V2 Bundles String Maps.
     * @param {Object.<string, Object>} bundleMap Map of bundleName to Strings Map
     * @returns {Object.<string, Object>} Map of bundleName to Strings Map
     */
    getV2StringMap(bundleMap = {}) {
      // For each V2 Bundle, ask it to add its named bundle map to the string map
      Object.entries(this.bundleV2Definitions)
        .forEach(([bundleName, bundleDefinition]) => {
          bundleDefinition.addStringMapToBundleMap(bundleName, bundleMap);
        });
      return bundleMap;
    }

    /**
     * Get the BundleV2Definition with name
     * @param {string} name name of the BundleV2Definition
     * @returns {BundleV2Definition}
     */
    getV2BundleDefinition(name) {
      return this.bundleV2Definitions[name];
    }

    /**
     * Get the (V2) Bundle strings maps for all extensions
     * @returns {Object.<string, Object.<string, Object>>} Map of extensionId to Map of bundleName to Strings Map
     */
    getExtensionsV2StringMaps() {
      return Object.fromEntries(Object.keys(this.extensionsBundleV2Definitions)
        .map((extId) => [extId, this.getExtensionV2StringMap(extId)]));
    }

    /**
     * Get the (V2) Bundle strings map for the extension
     * @param {string} extId
     * @param {Object.<string, Object>} bundleMap  Map of bundleName to Strings Map
     * @returns {Object.<string, Object>} Map of bundleName to Strings Map
     */
    getExtensionV2StringMap(extId, bundleMap = {}) {
      const extensionBundleV2Definitions = this.extensionsBundleV2Definitions[extId];
      if (!extensionBundleV2Definitions) {
        return bundleMap;
      }

      // For each V2 Extension Bundle, ask it to add its named bundle map to the string map
      Object.entries(extensionBundleV2Definitions)
        .forEach(([bundleName, bundleDefinition]) => {
          bundleDefinition.addStringMapToBundleMap(bundleName, bundleMap);
        });
      return bundleMap;
    }

    /**
     * Get the BundleV2Definition for the extension with name
     * @param {string} extId id of the extension
     * @param {string} name name of the BundleV2Definition
     * @returns {BundleV2Definition}
     */
    getExtensionV2BundleDefinition(extId, name) {
      return this.extensionsBundleV2Definitions[extId][name];
    }

    /**
     * Load an array of (non-null) BundleDefinition.
     * Falsy paths should be ignored/skipped (VBS-2097)
     * The bundlesModel is created, and load is initiated.
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {Object} [metadata] the "translations" or "imports" : "translations" configuration metadata
     * @param {string} [referencePath] the directory of the referring container
     * @param {Object} [options] Special directives for the bundle definitions
     * @param {Object} [options.initParams] Application's initParams
     * @param {boolean} [options.preferV1] If V1 translations config is present, ignore V2 translations config.
     * @param {boolean} [options.proxyBundles] Make proxies for bundles and don't wait for the bundles to be loaded
     * @param {Object} [options.replacementValues] (V1) map of key/value pairs for expressions in path
     * @param {boolean} [options.allowSelfRelative] (V1) let the container JSON use "./" to mean 'relative to the
     * same folder'
     * @param {boolean} [options.isUnrestrictedRelative] (V1) you can reach outside of your current folder
     * (only app-flow)
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     * @param {Object} [extension] the extension containing this BundlesModel (or null for 'base')
     * @returns {BundlesModel}
     */
    static createBundlesModel(runtimeEnv, metadata = {}, referencePath = '', options = {}, extension = null) {
      const bundlesModel = new BundlesModel(runtimeEnv, referencePath, options, extension);

      // Has V1 translations declaration
      // "translations": {
      //   "<bundleName>": {
      //     "path": "<bundlePath>"
      //   }
      // }
      if (metadata.translations) {
        bundlesModel.addBundleV1Definitions(metadata.translations);
      }

      const selfId = extension ? extension.id : Constants.ExtensionFolders.BASE;
      const v2Translations = metadata.imports && metadata.imports.translations;
      const hasLocalBundleLocation = options.location && (selfId !== Constants.ExtensionFolders.BASE);

      /** @type {V2ExtensionBundleDeclaration[]} */
      let extensionBundleDeclarations = [];
      /** @type {V2LocalBundleDeclaration[]} */
      let localBundleDeclarations = [];

      // Parse V2 declarations up front so later processing can coordinate between legacy imports and local
      // declarations.
      if (v2Translations) {
        if (metadata.translations && options.preferV1) {
          // VBS-36818: ignore imported translations in business rules if we find v1 translations.
          logger.warn('Encountered both v1 and v2 translations declarations. Ignoring the v2 declaration.');
        } else {
          extensionBundleDeclarations = BundlesModel.getExtensionsBundleDeclarations(v2Translations, selfId);
        }

        // preferV1 suppresses explicit local/scoped declarations, but the inferred $default bundle is still
        // processed below. Metadata-rules rely on this to keep scoped V2 imports disabled while loading $default.
        if (hasLocalBundleLocation && !options.preferV1) {
          localBundleDeclarations = BundlesModel.getLocalBundleDeclarations(
            extension,
            v2Translations,
            options,
          );
        }
      }
      if (hasLocalBundleLocation) {
        // Look up $default separately because it is inferred from the default bundle resource, not imports metadata.
        const defaultBundleDeclaration = BundlesModel.getDefaultLocalBundleDeclaration(extension, options);
        if (defaultBundleDeclaration) {
          localBundleDeclarations.push(defaultBundleDeclaration);
        }
      }

      if (extensionBundleDeclarations.length || localBundleDeclarations.length) {
        // Collect extension ids from the parsed declarations so extension lookup/recovery can run before declaration
        // processing attaches bundle-loading promises.
        const extensionIds = BundlesModel.getBundleDeclarationExtensionIds(
          extensionBundleDeclarations,
          localBundleDeclarations,
        );
        const extensionPromises = BundlesModel.getExtensions(extensionIds, selfId);

        // Has (OG) V2 translations declaration
        // "imports": {
        //   "translations": {
        //     "self": [<bundleName>,...],
        //     "<extId>": [<bundleName>,...],
        //     "base": [<bundleName>,...]
        //   }
        // }
        //
        // Assemble the corresponding bundle name/path information from the translation-config declaration
        // "translations": {
        //   "<bundleName>": {
        //     "path": "<bundlePath>"
        //   }
        // }
        if (extensionBundleDeclarations.length) {
          // Attach async bundle-declaration promises for each imported extension after the declarations are known.
          const extsBundleDeclarations = BundlesModel.processExtensionsBundleDeclarations(
            runtimeEnv,
            extensionBundleDeclarations,
            extensionPromises,
          );
          // Get the bundle declarations for the extensions and add them to the bundle model
          // We extract just the bundle declarations listed in imports.translations. There could be more bundle
          // declarations in an extension, but aren't used by this BundlesModel
          extensionBundleDeclarations.forEach(({ extId, importId }) => {
            bundlesModel.addBundleV2Definitions(importId, v2Translations[importId], extsBundleDeclarations[extId]);
          });
        }

        // Has (New Style) V2 translations declaration
        // "imports": {
        //   "translations": {
        //     "<bundleName>": {
        //       "path": "(self|<extId>|global):[$translations/]resourceName-i18n"
        //     }
        //   }
        // }
        // Assemble V2 bundles from the location for this model.
        // (We can only do this on extensions, since we check the extension file manifest)
        if (localBundleDeclarations.length) {
          // Attach the async bundle-declaration promises after all local declarations are known.
          const bundleDeclarations = BundlesModel.processLocalBundleDeclarations(
            runtimeEnv,
            extension,
            localBundleDeclarations,
            extensionPromises,
          );
          bundlesModel.addLocalBundleV2Definitions(bundleDeclarations);
        }
      }

      // Initiate the load of the BundlesModel, but don't block on it
      bundlesModel.load();

      return bundlesModel;
    }

    /**
     * Load an array of (non-null) BundleDefinition.
     * Falsy paths should be ignored/skipped (VBS-2097)
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {Object} [metadata] the "translations" or "imports" : "translations" configuration metadata
     * @param {string} [referencePath] the directory of the referring container
     * @param {Object} [options] Special directives for the bundle definitions
     * @param {Object} [options.initParams] Application's initParams
     * @param {boolean} [options.preferV1] If V1 translations config is present, ignore V2 translations config.
     * @param {boolean} [options.proxyBundles] Make proxies for bundles and don't wait for the bundles to be loaded
     * @param {Object} [options.replacementValues] (V1) map of key/value pairs for expressions in path
     * @param {boolean} [options.allowSelfRelative] (V1) let the container JSON use "./" to mean 'relative to the
     * same folder'
     * @param {boolean} [options.isUnrestrictedRelative] (V1) you can reach outside of your current folder
     * (only app-flow)
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     * @param {Object} [extension] the extension containing this BundlesModel (or null for 'base')
     * @returns {Promise<BundlesModel>}
     */
    static loadBundlesModel(runtimeEnv, metadata = {}, referencePath = '', options = {}, extension = null) {
      const bundlesModel = BundlesModel.createBundlesModel(runtimeEnv, metadata, referencePath, options, extension);

      return bundlesModel.load();
    }

    /**
     * Collect extension ids that need registry lookup/recovery for parsed V2 declarations.
     *
     * @param {V2ExtensionBundleDeclaration[]} extensionBundleDeclarations legacy/imported V2 declarations
     * @param {V2LocalBundleDeclaration[]} localBundleDeclarations local/default V2 declarations
     * @returns {Set<string>} extension ids used by parsed V2 declarations
     * @private
     */
    static getBundleDeclarationExtensionIds(extensionBundleDeclarations, localBundleDeclarations) {
      const extensionIds = new Set([Constants.ExtensionFolders.BASE]);

      extensionBundleDeclarations.forEach(({ extId }) => {
        extensionIds.add(extId);
      });

      localBundleDeclarations.forEach(({ extId }) => {
        extensionIds.add(extId);
      });

      return extensionIds;
    }

    /**
     * Parse old-style V2 imports into extension bundle declarations.
     *
     * @param {Object} v2Translations imports.translations metadata
     * @param {string} selfId extension id to use when an import references self
     * @returns {V2ExtensionBundleDeclaration[]} extension bundle declarations with resolved provider ids
     * @private
     */
    static getExtensionsBundleDeclarations(v2Translations, selfId) {
      return Object.entries(v2Translations)
        // Empty legacy arrays do not request any bundles, so avoid resolving/recovering unused providers.
        .filter(([, value]) => Array.isArray(value) && value.length > 0)
        .map(([importId]) => ({
          extId: importId === Constants.ExtensionFolders.SELF ? selfId : importId,
          importId,
        }));
    }

    /**
     * Load the bundle name/declaration information from one extension translations-config.
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {V2ExtensionBundleDeclaration} declaration extension bundle declaration to process
     * @param {Object.<string, Promise<Object|null>>} extensionPromises map of extension id to extension Promise
     * @returns {Promise<ExtensionTranslationsConfig>} extension bundles Promise
     * @private
     */
    static processExtensionsBundleDeclaration(runtimeEnv, declaration, extensionPromises) {
      const { extId } = declaration;

      // Wait for the lookup/recovery promise before consulting the cache. A registry reload can invalidate extension
      // bundle declarations, so an early cache hit could reuse stale translation metadata.
      return extensionPromises[extId].then((resolvedExtension) => {
        // See if we've already located the bundles declaration for each extension (and 'base').
        // If not, load the translations configuration.
        let extensionBundleDeclarations = BundlesModel.extensionsBundleDeclarations[extId];
        if (!extensionBundleDeclarations) {
          let translationsInfo;
          let extension = null;
          if (extId === Constants.ExtensionFolders.BASE) {
            // Load the translations-config for the base app.
            const baseTranslationsInfo = Promise.allSettled([
              runtimeEnv.getTextResource('resources/translations/translations-config.json')
                .then((config) => JSON.parse(config).translations),
              BundlesModel.deriveSupportedLocale(runtimeEnv, null, 'resources/translations/supportedLocales'),
            ]);

            translationsInfo = baseTranslationsInfo;
          } else {
            // Load the translations-config for the extension.
            translationsInfo = Promise.resolve(resolvedExtension)
              .then((extensionCandidate) => {
                const translationsConfigPath = `${Constants.DefaultPaths.TRANSLATIONS}translations-config.json`;
                extension = extensionCandidate && extensionCandidate.hasFile(translationsConfigPath)
                  ? extensionCandidate
                  : null;
                if (!extension) {
                  logger.warn(`No translations-config found for extension '${extId}'`);
                  delete BundlesModel.extensionsBundleDeclarations[extId];
                  BundleV2Definition.clearEmptyBundles(extId);
                  return Promise.allSettled([
                    Promise.resolve({}),
                    Promise.resolve(null),
                  ]);
                }

                // Make sure the extension is initialized, so its RequireJS mappings are set.
                return extension.init()
                  .then(() => Promise.allSettled([
                    runtimeEnv.getExtensionTextResource(`${extension.baseUrl}translations/translations-config.json`)
                      .then((config) => JSON.parse(config).translations),
                    BundlesModel.deriveSupportedLocale(runtimeEnv, extension, 'translations/self/supportedLocales'),
                  ]));
              });
          }

          // Extract the translations map and selected locale into the normalized declaration shape:
          // "translations": {
          //   "<bundleName>": {
          //     "path": "<bundlePath>"
          //   }
          // },
          // "locale": "en"
          extensionBundleDeclarations = translationsInfo
            .then(([translationsConfig, supportedLocale]) => {
              /** @type {ExtensionTranslationsConfig} */
              const config = {
                extension,
                translations: {},
              };

              if (translationsConfig.status === 'rejected') {
                logger.error(`Failed to load translations-config for ${extId}`,
                  translationsConfig.reason.message || translationsConfig.reason);
                return config;
              }
              config.translations = translationsConfig.value;

              if (supportedLocale.status === 'rejected') {
                logger.warn(`Failed to load supportedLocales for ${extId}`,
                  supportedLocale.reason.message || supportedLocale.reason);
                return config;
              }

              config.locale = supportedLocale.value;

              return config;
            });

          BundlesModel.extensionsBundleDeclarations[extId] = extensionBundleDeclarations;
        }

        return extensionBundleDeclarations;
      });
    }

    /**
     * Load the bundles name/declaration information from the translations-config in the extensions (including 'base')
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {V2ExtensionBundleDeclaration[]} declarations extension bundle declarations to process
     * @param {Object.<string, Promise<Object|null>>} extensionPromises map of extension id to extension Promise
     * @returns {Object.<string, Promise<ExtensionTranslationsConfig>>} Map of extensionId to extension bundles Promise
     * @private
     */
    static processExtensionsBundleDeclarations(runtimeEnv, declarations, extensionPromises) {
      /** @type {Object.<string, Promise<ExtensionTranslationsConfig>>} */
      const bundleDeclarations = {};
      const registry = BundlesModel.application.extensionRegistry;

      declarations.forEach((declaration) => {
        bundleDeclarations[declaration.extId] = registry.resolveWithStableManifest(
          () => BundlesModel.processExtensionsBundleDeclaration(
            runtimeEnv,
            declaration,
            extensionPromises,
          ),
          BundlesModel.invalidateTranslationCaches,
        );
      });

      return bundleDeclarations;
    }

    /**
     * Get a map of extension ids to Promises for the corresponding registry extensions. Missing extensions trigger a
     * shared full-manifest recovery pass, then all requested extensions resolve from stable registry metadata.
     *
     * A full-manifest recovery can refresh extension objects beyond the missing provider that triggered it. This method
     * therefore treats the requested ids as a batch: start every lookup immediately, wait for any resulting manifest
     * work to settle, invalidate translation metadata if the registry changed, then re-read each extension from the
     * stable registry before resolving its Promise.
     *
     * @param {Set<string>} extIds extension ids expected to be present in the registry
     * @param {string} [ownerId] extension id that references the providers
     * @returns {Object.<string, Promise<Object|null>>} map of extension id to extension Promise
     * @private
     */
    static getExtensions(extIds, ownerId) {
      /** @type {Object.<string, Promise<Object|null>>} */
      const extensionPromises = {};
      const registry = BundlesModel.application.extensionRegistry;

      // Without a registry there is no reload/stability boundary; return resolved placeholders.
      if (!registry) {
        extIds.forEach((extId) => {
          extensionPromises[extId] = Promise.resolve(null);
        });
        return extensionPromises;
      }

      const registryExtIds = new Set(extIds);
      registryExtIds.delete(Constants.ExtensionFolders.BASE);

      // Capture the generation before any recovery so we can clear translation caches derived from stale metadata.
      const initialManifestGeneration = registry.manifestGeneration;
      const canRecover = typeof registry.loadFullManifest === 'function'
        && (typeof registry.isPartialDigest !== 'boolean' || registry.isPartialDigest);

      /**
       * @param {string} extId provider extension id
       * @returns {boolean} true when owner dependency metadata does not expose the provider
       */
      function isUndeclaredProvider(extId) {
        return !!(ownerId && ownerId !== Constants.ExtensionFolders.BASE && ownerId !== extId
          && !registry.isDependent(ownerId, extId));
      }

      const resolveExtension = async (extId) => {
        // Await initial registry loading before detecting missing providers
        await registry.getExtensions();

        const extension = registry.getExtensionById(extId);
        if (extension || !canRecover) {
          return extension || null;
        }

        // Recovery failures leave the partial registry usable. Log the diagnostic and let callers see a null provider
        // so translation-specific retry cleanup can stay in the declaration-loading path.
        if (isUndeclaredProvider(extId)) {
          logger.error('Extension', ownerId, 'references translation provider extension', extId,
            'without declaring it as a dependency.');
        }
        try {
          await registry.loadFullManifest(extId);
        } catch (error) {
          logger.warn(`Failed to recover extension '${extId}'`, error);
        }
        return registry.getExtensionById(extId) || null;
      };

      /** @type {Object.<string, Promise<Object|null>>} */
      const rawExtensionPromises = {};
      registryExtIds.forEach((extId) => {
        // Start each lookup now so missing providers share the same full-manifest recovery when possible.
        rawExtensionPromises[extId] = resolveExtension(extId);
      });

      const stableManifestPromise = Promise.allSettled(Object.values(rawExtensionPromises))
        .then(() => registry.whenManifestStable())
        .then((stableGeneration) => {
          if (stableGeneration !== initialManifestGeneration) {
            BundlesModel.invalidateTranslationCaches();
          }
        });

      // Keep the base entry on the same stability boundary so base declarations wait for any registry reload.
      extensionPromises[Constants.ExtensionFolders.BASE] = stableManifestPromise.then(() => null);

      registryExtIds.forEach((extId) => {
        // Expose per-id Promises, but gate each one on batch stability and re-read from the registry. A recovery may
        // refresh already-present Extension objects, so returning the object captured before recovery would be stale.
        extensionPromises[extId] = rawExtensionPromises[extId]
          .then(() => stableManifestPromise)
          .then(() => registry.getExtensionById(extId) || null);
      });

      return extensionPromises;
    }

    /**
     * Invalidate extension translation declarations derived from a previous manifest generation. Supported-locale
     * resources stay cached because they are scoped to an already-loaded extension path and do not change when
     * unrelated extensions are recovered.
     *
     * This only affects future declaration lookups. Already-created BundleV2Definition instances may keep their current
     * resolved maps; refreshing loaded bundles after manifest recovery is a separate runtime behavior.
     * @private
     */
    static invalidateTranslationCaches() {
      BundlesModel.extensionsBundleDeclarations = {};
    }

    /**
     * Get the path for the $default translations bundle, if it exists.
     * @param {Object} extension the extension containing this BundlesModel
     * @param {Object} options Special directives for the bundle definitions
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     * @returns {string|null}
     * @private
     */
    static getDefaultTranslationPath(extension, options) {
      // Determine if there is a default bundle at the location.
      // If so, there would exist a root bundle, so we don't need to resolve locale yet to determine if a
      // default bundle is present.
      // (Unit tests may not mock a full Extension api)
      if (extension.hasFile
      && extension.hasFile(`${options.location}resources/translations/${options.defaultBundleResourceName}-i18n.js`)) {
        return `${Constants.ExtensionFolders.SELF}:${options.defaultBundleResourceName}-i18n`;
      }
      return null;
    }

    /**
     * Parse one local/default V2 translation declaration.
     *
     * @param {Object} extension the extension containing this BundlesModel
     * @param {string} bundleName name of the bundle declaration
     * @param {Object} bundleDefinition bundle declaration metadata
     * @param {string} bundleDefinition.path V2 bundle path
     * @param {Object} options Special directives for the bundle definitions
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @returns {V2LocalBundleDeclaration|null} parsed V2 local bundle declaration
     * @private
     */
    static getLocalBundleDeclaration(extension, bundleName, bundleDefinition, options) {
      // Analyze the bundle path
      const match = V2_LOCAL_DECLARATION_REGEX.exec(bundleDefinition.path);
      if (!match) {
        logger.error(`Not a v2 translations declaration '${bundleName}' ${bundleDefinition.path}`);
        return null;
      }

      let localeLocation;
      let bundleLocation;
      let prefix;
      const [, declarationPrefix, topLevelScope, resourceName] = match; // bundleResourceName-i18n

      if (topLevelScope) { // Top-Level path, at $translations
        // declarationPrefix is (self|extId|global):$translations/
        prefix = topLevelScope; // self|extId|global
        // Location within this extension
        localeLocation = (prefix === Constants.GLOBAL_PREFIX)
          ? 'resources/translations/'
          : 'translations/self/';
        bundleLocation = '';
      } else if (declarationPrefix === 'self:') { // V2 Local path
        prefix = Constants.ExtensionFolders.SELF;
        localeLocation = `${options.location}resources/translations/`;
        // location starts at root of extension, not relative to /translations/self/
        // (See bundleV2Definition.js V2PathHandler)
        bundleLocation = `/${localeLocation}`;
      } else {
        // A scoped translations resource, only self: is supported
        logger.error(`Not a v2 translations declaration '${bundleName}' ${bundleDefinition.path}`);
        return null;
      }

      // Which Extension?
      let extId;
      if (prefix === Constants.ExtensionFolders.SELF) {
        extId = extension.id;
      } else if (prefix === Constants.GLOBAL_PREFIX) {
        extId = Constants.ExtensionFolders.BASE;
      } else {
        extId = prefix;
      }

      // Identify the bundle (not just name, as there could be several '$default').
      const bundleKey = `${options.location}:${bundleDefinition.path}`;

      /** @type V2LocalBundleDeclaration */
      return {
        bundleKey,
        bundleName,
        path: bundleDefinition.path,
        extId,
        bundleLocation,
        localeLocation,
        prefix,
        resourceName,
      };
    }

    /**
     * Parse the default V2 translation declaration when the default bundle resource exists.
     *
     * @param {Object} extension the extension containing this BundlesModel
     * @param {Object} options Special directives for the bundle definitions
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @param {string} [options.defaultBundleResourceName] (V2) Filename of default bundle in location
     * @returns {V2LocalBundleDeclaration|null} parsed default V2 local bundle declaration
     * @private
     */
    static getDefaultLocalBundleDeclaration(extension, options) {
      const defaultBundlePath = BundlesModel.getDefaultTranslationPath(extension, options);
      if (!defaultBundlePath) {
        return null;
      }

      return BundlesModel.getLocalBundleDeclaration(
        extension,
        Constants.TranslationsResources.DEFAULT_BUNDLE_NAME,
        { path: defaultBundlePath },
        options,
      );
    }

    /**
     * Parse local V2 translation declarations from a container's imports metadata.
     *
     * @param {Object} extension the extension containing this BundlesModel
     * @param {Object} v2Translations imports.translations metadata
     * @param {Object} options Special directives for the bundle definitions
     * @param {string} [options.location] (V2) Location (directory) of bundles
     * @returns {V2LocalBundleDeclaration[]} parsed V2 local bundle declarations
     * @private
     */
    static getLocalBundleDeclarations(extension, v2Translations, options) {
      /** @type V2LocalBundleDeclaration[] */
      const declarations = [];
      /** @type {TranslationsConfig} */
      const translationDeclarations = Object.entries(v2Translations || {})
        .filter(([, value]) => Utils.isObject(value))
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      Object.entries(translationDeclarations).forEach(([bundleName, bundleDefinition]) => {
        const declaration = BundlesModel.getLocalBundleDeclaration(extension, bundleName, bundleDefinition, options);
        if (declaration) {
          declarations.push(declaration);
        }
      });

      return declarations;
    }

    /**
     * Build the async bundle declaration promise for one parsed local/default V2 translation declaration.
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {Object} extension the extension containing this BundlesModel
     * @param {V2LocalBundleDeclaration} declaration parsed V2 local bundle declaration
     * @param {Object.<string, Promise<Object|null>>} extensionPromises map of extension id to extension Promise
     * @returns {Promise<ExtensionTranslationsConfig>} bundle-loading declaration promise
     * @private
     */
    static processLocalBundleDeclaration(runtimeEnv, extension, declaration, extensionPromises) {
      const {
        localeLocation,
        extId,
        bundleLocation,
        resourceName,
        bundleKey,
      } = declaration;

      /**
       * Resolve the extension that supplies the bundle for this parsed local V2 declaration.
       * @param {Object|null} resolvedExtension registry-stable extension resolved for the declaration extId
       * @returns {Promise<Object|null>} initialized bundle provider extension, or null for base/global bundles
       */
      function getBundleExtension(resolvedExtension) {
        if (declaration.prefix === Constants.ExtensionFolders.SELF) {
          return Promise.resolve(resolvedExtension || extension);
        }
        if (declaration.prefix === Constants.GLOBAL_PREFIX) {
          return Promise.resolve(null);
        }

        if (!resolvedExtension) {
          throw new Error(
            `Translation provider extension '${declaration.extId}' is unavailable after full manifest recovery.`,
          );
        }

        // Make sure the extension is initialized
        // (it might not have been initialized by extension.init(), if it is a missing dependency extension)
        return resolvedExtension.init()
          .then(() => resolvedExtension);
      }

      // Wait for this declaration's lookup/recovery before initializing or checking the supported-locale cache.
      // getExtensions() supplies an entry for every parsed declaration extension id, plus base.
      return extensionPromises[extId]
        .then((resolvedExtension) => {
          // Make sure the owning extension is initialized, so its require mappings are set. When this is a self
          // declaration, prefer the registry-stable owner returned by getExtensions().
          const ownerExtension = declaration.prefix === Constants.ExtensionFolders.SELF
            ? (resolvedExtension || extension)
            : extension;
          return Promise.resolve(ownerExtension && ownerExtension.init())
            // Get and init the extension for this bundle declaration (if it is different from the owner extension)
            .then(() => getBundleExtension(resolvedExtension));
        })
        .then((bundleExtension) => BundlesModel.deriveSupportedLocale(runtimeEnv, bundleExtension,
          `${localeLocation}supportedLocales`)
          .catch((err) => {
            logger.warn(`Failed to load supportedLocales for '${extId}' ${localeLocation}`, err);
            return null;
          })
          .then((locale) => {
            // If no matching locale is found, or locale metadata cannot be loaded, use the root bundle.
            const path = locale ? `${bundleLocation}${locale}/${resourceName}` : `${bundleLocation}${resourceName}`;

            /** @type {ExtensionTranslationsConfig} */
            const extBundleDeclaration = {
              extension: bundleExtension,
              translations: {
                [bundleKey]: {
                  path,
                },
              },
              locale,
            };
            return extBundleDeclaration;
          }))
        .catch((error) => {
          BundleV2Definition.clearEmptyBundles(extId);
          throw error;
        });
    }

    /**
     * Build async bundle declaration promises for parsed local/default V2 translation declarations.
     *
     * @param {Object} runtimeEnv RuntimeEnvironment
     * @param {Object} extension the extension containing this BundlesModel
     * @param {V2LocalBundleDeclaration[]} declarations parsed V2 local bundle declarations
     * @param {Object.<string, Promise<Object|null>>} extensionPromises map of extension id to extension Promise
     * @returns {V2LocalBundleDeclaration[]} declarations with bundle-loading promises attached
     * @private
     */
    static processLocalBundleDeclarations(runtimeEnv, extension, declarations, extensionPromises) {
      const registry = BundlesModel.application.extensionRegistry;

      return declarations.map((declaration) => {
        // Resolve each local declaration against stable manifest metadata before exposing it to BundleV2Definition.
        // eslint-disable-next-line no-param-reassign
        declaration.extBundleDeclarations = registry.resolveWithStableManifest(
          () => BundlesModel.processLocalBundleDeclaration(
            runtimeEnv,
            extension,
            declaration,
            extensionPromises,
          ),
          BundlesModel.invalidateTranslationCaches,
        );
        return declaration;
      });
    }

    /**
     * Derive a supported local for the current locale.
     * If no supported locales, or a supported locale is not found, return null to indicate fallback to root.
     * @package {Object} runtimeEnv
     * @param {Object} extension
     * @param {string} path
     * @returns {Promise<string | null>}
     * @private
     */
    static deriveSupportedLocale(runtimeEnv, extension, path) {
      const extId = extension ? extension.id : Constants.ExtensionFolders.BASE;
      if (!BundlesModel.supportedLocales[extId]) {
        BundlesModel.supportedLocales[extId] = {};
      }

      if (!BundlesModel.supportedLocales[extId][path]) {
        const loadSupportedLocales = (extId === Constants.ExtensionFolders.BASE)
          ? runtimeEnv.getModuleResource(path)
          : runtimeEnv.getExtensionModuleResource(extension.baseUrl + path);

        BundlesModel.supportedLocales[extId][path] = loadSupportedLocales
          .then((supportedLocales) => {
            // Get the locale that ConfigLoader has set to ojConfig (or that JET has derived if we haven't set it).
            const locale = ojConfig.getLocale();

            // ES6 module { default: ['en'] }
            const locales = new Set(supportedLocales.default || supportedLocales);
            if (locale && locales.size) {
              // match a supported locale, or force null
              return UnsafeMatchTranslationBundle.matchTranslationBundle([locale], locales) || null;
            }

            return null;
          });
      }

      return BundlesModel.supportedLocales[extId][path];
    }

    /**
     * @private
     */
    static isProxyBundles(options) {
      // This BundlesModel explicitly indicates whether or not to proxy
      if ((typeof options.proxyBundles) !== 'undefined') {
        return !!options.proxyBundles;
      }

      // Overridden in localStorage?
      let proxyBundlesStr = BundlesModel.storageInterface
        && BundlesModel.storageInterface.getItem(PROXY_BUNDLES_STORAGE_ID);
      if (proxyBundlesStr !== null) {
        return proxyBundlesStr === 'true';
      }

      proxyBundlesStr = BundlesModel.storageInterface
        && BundlesModel.storageInterface.getItem(APPLICATION_PROXY_BUNDLES_STORAGE_ID);
      if (proxyBundlesStr !== null) {
        return proxyBundlesStr === 'true';
      }

      // Overridden in vbInitConfig?
      const initConfigProxyBundles = globalThis.vbInitConfig
        && globalThis.vbInitConfig.translations
        && globalThis.vbInitConfig.translations.proxyBundles;
      if ((typeof initConfigProxyBundles) !== 'undefined') {
        return !!initConfigProxyBundles;
      }

      // Default to true if not specified
      return true;
    }

    /**
     * Test helper
     * Reset cached information
     */
    static reset() {
      BundlesModel.application = null;
      BundlesModel.extensionsBundleDeclarations = {};
      BundlesModel.supportedLocales = {};
      BundlesModel.storageInterface = globalThis.localStorage;
      BundleV2Definition.reset();
    }
  }

  /**
   * Reference to Application, so all Bundles can have access.
   * This is assigned in Application.load() (vb/private/stateManagement/applicationClass.js)
   */
  BundlesModel.application = null;

  /**
   * Map of extensionId to Map of bundle name/declaration
   * @type {Object.<string, Promise<ExtensionTranslationsConfig>>} Map of extensionId to Map of bundle name/declaration
   */
  BundlesModel.extensionsBundleDeclarations = {};

  /**
   * Map of extensionId to map of path to supportedLocale for that path
   * @type {Object.<string, Object.<string, Promise<string>>>}
   */
  BundlesModel.supportedLocales = {};

  /**
   * Storage for option overrides.
   * Having it defined as a property allows tests to inject a mock storage
   */
  BundlesModel.storageInterface = globalThis.localStorage;

  BundlesModel.PROXY_BUNDLES_STORAGE_ID = PROXY_BUNDLES_STORAGE_ID;

  // For testing
  BundlesModel.V2_LOCAL_DECLARATION_REGEX = V2_LOCAL_DECLARATION_REGEX;

  return BundlesModel;
});

